import { useCallback, useEffect, useRef } from 'react'

import { AutoHideProps } from '@/types/componentsPropsTypes'

import { useDebounce } from './useDebounce'
import { useThrottle } from './useThrottle'

export function useAutoHide({ show, onChange, options = {} }: AutoHideProps) {
  const { onShow, onHide, showDuration = 5, useClickHandler = false } = options
  const mouseOver = useRef<boolean>(true) // initialValue = true, in Order to display the controls, even if there is a problem or other Overlay at the initial-page-load

  const handleHide = useCallback(() => {
    onHide?.()
    onChange?.(false)
  }, [onChange, onHide])

  const handleShow = useCallback(() => {
    onShow?.()
    onChange?.(true)
  }, [onChange, onShow])

  const throttledDelayHide = useDebounce(() => {
    handleHide()
  }, showDuration * 1000)

  const handleMove = useCallback(() => {
    if (!useClickHandler) {
      handleShow?.()
      if (showDuration > 0) {
        throttledDelayHide()
      }
    }
  }, [useClickHandler, handleShow, showDuration, throttledDelayHide])

  const throttledHandleMove = useThrottle(handleMove, 100)

  const handleLeave = useCallback(() => {
    if (useClickHandler) {
      return
    }
    mouseOver.current = false
    handleHide()
  }, [handleHide, useClickHandler])

  const handleEnter = useCallback(() => {
    if (useClickHandler) {
      return
    }
    mouseOver.current = true
    handleShow()
  }, [handleShow, useClickHandler])

  const handleClick = useCallback(() => {
    if (!useClickHandler) {
      return
    }
    if (show) {
      handleHide()
    } else {
      handleShow()
    }
  }, [handleHide, handleShow, show, useClickHandler])

  useEffect(() => {
    if (showDuration > 0) {
      throttledDelayHide()
    }
  }, [showDuration, throttledDelayHide])

  useEffect(() => {
    return () => throttledDelayHide.cancel?.()
  }, [])

  return {
    handlers: {
      onMouseMove: throttledHandleMove,
      onMouseLeave: handleLeave,
      onMouseEnter: handleEnter,
      onClick: handleClick,
      onKeyDown: throttledHandleMove,
    },
  }
}
